import { Text as ChakraText } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { devWarn } from '~tools/devLog'
import { fontSizes, fontStyles, TFontStyle, TTextFontSize } from '~ui/styleGuide/fontStyles'
import { textShadows, TTextShadow } from '~ui/styleGuide/shadows'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'
import {
  TChakraAsProp,
  TChakraDimensionsProps,
  TChakraDisplayProps,
  TChakraTypographyProps,
  THtmlElementProp,
} from '~ui/typings/chakra'
import { responsiveFontByWidth, TRespFontByWithOptions } from '~ui/utils/responsiveFonts'

import { TUseTextPropsOptions, useTextProps } from '../hooks/useTextProps'

type TInnerHTML = Pick<PropsOf<typeof StyledText>, 'dangerouslySetInnerHTML'>

type TChakraProps = TChakraTypographyProps &
  TChakraDisplayProps &
  TChakraAsProp &
  Pick<TChakraDimensionsProps, 'width' | 'maxWidth' | 'minWidth'> &
  Pick<THtmlElementProp, 'title'>

type TStyledProps = {
  /**
   * responsive font size props
   * define min & max viewport widths and values that should be used bellow and above those breakpoints
   * the middle ground will be calculated dynamically based on the actual viewport width
   * ! all of the values has to use the same unit
   */
  responsiveSize?: TRespFontByWithOptions
}

type TProps = Children &
  TChakraProps &
  TStyledProps &
  TInnerHTML &
  TUseTextPropsOptions &
  TestingProps & {
    /**
     * key of the predefined font styles
     * might set attributes like font family, weight, casing, ...
     */
    fontStyle?: TFontStyle
    /**
     * key of the predefined text font sizes
     */
    fontSize?: TTextFontSize
    /**
     * space inside the text box,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * if true the default values this component sets won’t be used (eg. fontSize/fontStyle)
     * you can still provider your own values as props if needed
     * this can be useful when nesting Text components
     */
    inherit?: boolean
    /**
     * key of the predefined text shadows
     */
    textShadow?: TTextShadow
  }

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `responsiveSize`,
}

const StyledText = styled(ChakraText, propForwardOptions)<TStyledProps>`
  ${({ responsiveSize }) => (responsiveSize ? responsiveFontByWidth(responsiveSize) : '')};
`

/**
 * Basic text component with automatic color selection (based on the consumed bgColor context & the text `contrast`),
 * and option for text truncating (with ...).
 * Font style (family, weight, ...) and size should be selected.
 */
export const Text: React.FC<TProps> = ({
  padding,
  children,
  fontSize,
  contrast,
  fontStyle,
  responsiveSize,
  inherit = false,
  truncateOnLines = 0,
  textShadow,
  dataTestId,
  ...chakraProps
}) => {
  const fontStyleWithDefault = fontStyle ?? `regular`
  const fontSizeWithDefault = fontSize ?? `m16`

  const { textColorValue, noOfLines, isTruncated } = useTextProps({ truncateOnLines, contrast })

  if (responsiveSize !== undefined && fontSize !== undefined) {
    devWarn(
      `You are using both responsiveSize and fontSize at once in Text component. Make up your mind (fontSize is now ignored)!`,
    )
  }

  const paddingValue = padding && getSpacingValue(padding)

  const fontStyleProps = inherit && !fontStyle ? {} : fontStyles[fontStyleWithDefault]
  const fontSizeValue = inherit && !fontSize ? `inherit` : fontSizes.text[fontSizeWithDefault]
  const textShadowValue = inherit && !textShadow ? `inherit` : textShadows[textShadow ?? `none`]
  const textColor = inherit && !contrast ? `inherit` : textColorValue

  return (
    <StyledText
      {...chakraProps}
      {...fontStyleProps}
      noOfLines={noOfLines}
      color={textColor}
      padding={paddingValue}
      fontSize={fontSizeValue}
      isTruncated={isTruncated}
      responsiveSize={responsiveSize}
      textShadow={textShadowValue}
      data-testid={dataTestId}
    >
      {children}
    </StyledText>
  )
}
